/**
 * Modale CER attivi — Caratteristiche del rifiuto .
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, endpoints } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChoiceCombobox, ChoiceSelect, FormField } from "@/components/forms";
import {
  normalizeCodificaItem,
  PROVENIENZA,
  STATO_FISICO,
  type RentriChoice,
} from "@/lib/rentriChoices";
import {
  type CerCatalogEntry,
  upsertCerCatalog,
} from "@/lib/cerCatalog";
import { formatEerDisplay } from "@/lib/movimento";

function toApiBody(
  codice: string,
  descrizione: string,
  statoFisico: string,
  provenienza: "U" | "S",
  scadenza: string,
  predefinito: boolean,
) {
  return {
    codice_eer: codice.trim().replace(/\./g, ""),
    descrizione: descrizione.trim() || codice,
    stato_fisico: statoFisico,
    provenienza,
    scadenza_analisi: scadenza || undefined,
    is_default: predefinito,
  };
}

function validate(
  codice: string,
  statoFisico: string,
  provenienza: string,
): string | null {
  if (!codice.trim()) return "Seleziona un codice CER dalla ricerca.";
  if (!statoFisico) return "Indica lo stato fisico del rifiuto.";
  if (!provenienza) return "Indica la provenienza (D.Lgs. 152/2006).";
  return null;
}

export function CerAttivoDialog({
  open,
  onOpenChange,
  entry,
  onSaved,
  useApi = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry?: CerCatalogEntry | null;
  onSaved: () => void;
  useApi?: boolean;
}) {
  const [codice, setCodice] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [statoFisico, setStatoFisico] = useState("S");
  const [provenienza, setProvenienza] = useState<"U" | "S">("U");
  const [scadenza, setScadenza] = useState("");
  const [predefinito, setPredefinito] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const eerQ = useQuery({
    queryKey: ["codifiche-eer-choices"],
    queryFn: async (): Promise<RentriChoice[]> => {
      const rows = await endpoints.codifiche.lookup("codici-eer");
      const list = Array.isArray(rows) ? rows : [];
      return list
        .map((r) => {
          const c = normalizeCodificaItem(r);
          if (!c) return null;
          return { ...c, value: c.value.replace(/\./g, "") };
        })
        .filter((x): x is RentriChoice => !!x);
    },
    staleTime: 24 * 60 * 60 * 1000,
    enabled: open,
  });

  const validationError = useMemo(
    () => validate(codice, statoFisico, provenienza),
    [codice, statoFisico, provenienza],
  );

  useEffect(() => {
    if (!open) return;
    setFieldError(null);
    if (entry) {
      setCodice(entry.codice);
      setDescrizione(entry.descrizione);
      setStatoFisico(entry.stato_fisico || "S");
      setProvenienza(entry.provenienza);
      setScadenza(entry.scadenza_analisi ?? "");
      setPredefinito(!!entry.predefinito);
    } else {
      setCodice("");
      setDescrizione("");
      setStatoFisico("S");
      setProvenienza("U");
      setScadenza("");
      setPredefinito(false);
    }
  }, [open, entry]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const err = validate(codice, statoFisico, provenienza);
      if (err) throw new Error(err);
      const body = toApiBody(codice, descrizione, statoFisico, provenienza, scadenza, predefinito);
      if (useApi) {
        if (entry?.id) {
          await endpoints.cerCatalog.update(entry.id, body);
        } else {
          await endpoints.cerCatalog.create(body);
        }
        return;
      }
      upsertCerCatalog({
        id: entry?.id,
        codice: body.codice_eer,
        descrizione: body.descrizione ?? body.codice_eer,
        stato_fisico: statoFisico,
        provenienza,
        scadenza_analisi: scadenza || undefined,
        predefinito,
      });
    },
    onSuccess: () => {
      toast.success(entry ? "CER aggiornato" : "CER aggiunto all'elenco preferiti");
      onSaved();
      onOpenChange(false);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 402) {
        setFieldError("Slot CER esauriti. Acquista un pacchetto da CER attivi.");
        toast.error("Acquista un pacchetto +5 CER per aggiungere altri codici.");
        return;
      }
      if (e instanceof ApiError && e.status === 409) {
        setFieldError(entry?.id ? e.message : "Questo codice CER è già presente nell'elenco.");
        return;
      }
      const msg = e instanceof Error ? e.message : "Errore salvataggio CER";
      setFieldError(msg);
      toast.error(msg);
    },
  });

  const isEdit = !!entry?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,680px)] max-w-lg flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{isEdit ? "Modifica CER" : "Nuovo CER"}</DialogTitle>
          <p className="text-xs text-muted-foreground">Caratteristiche del rifiuto</p>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {fieldError && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {fieldError}
            </p>
          )}

          {isEdit ? (
            <FormField label="Codice CER" required>
              <Input
                value={formatEerDisplay(codice)}
                readOnly
                className="font-mono bg-muted"
              />
            </FormField>
          ) : (
            <FormField
              label="Codice CER"
              required
              description="Digita almeno un carattere per cercare nel catalogo ministeriale."
            >
              {(f) => (
                <ChoiceCombobox
                  id={f.id}
                  value={codice}
                  onChange={(v, choice) => {
                    setCodice(v.replace(/\./g, ""));
                    if (choice?.description) setDescrizione(choice.description);
                    setFieldError(null);
                  }}
                  options={eerQ.data ?? []}
                  loading={eerQ.isLoading}
                  formatValue={formatEerDisplay}
                  placeholder="Inserisci un carattere per ricercare…"
                />
              )}
            </FormField>
          )}

          <FormField label="Descrizione" description="Testo libero associato al codice (opzionale).">
            {(f) => (
              <Input
                id={f.id}
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                placeholder="Descrizione del rifiuto"
              />
            )}
          </FormField>

          <FormField label="Stato fisico" required>
            {(f) => (
              <ChoiceSelect
                id={f.id}
                value={statoFisico}
                onChange={(v) => setStatoFisico(v || "S")}
                options={STATO_FISICO}
                required
              />
            )}
          </FormField>

          <FormField
            label="Provenienza"
            required
            description="Classificazione ai sensi del D.Lgs. 152/2006."
          >
            {(f) => (
              <ChoiceSelect
                id={f.id}
                value={provenienza}
                onChange={(v) => setProvenienza((v || "U") as "U" | "S")}
                options={PROVENIENZA}
                required
              />
            )}
          </FormField>

          <FormField
            label="Scadenza analisi CER"
            description="Facoltativa — utile per promemoria su analisi periodiche."
          >
            {(f) => (
              <Input
                id={f.id}
                type="date"
                value={scadenza}
                onChange={(e) => setScadenza(e.target.value)}
              />
            )}
          </FormField>

          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={predefinito}
              onChange={(e) => setPredefinito(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            Imposta come CER predefinito
          </label>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t pt-4 sm:justify-between">
          <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (validationError) {
                setFieldError(validationError);
                return;
              }
              saveMut.mutate();
            }}
            disabled={saveMut.isPending || !!validationError}
          >
            {saveMut.isPending ? "Salvataggio…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
